export const educationStoreModuleName = 'educationStore';

function clone(value) {
  if (value === undefined) return undefined;

  return JSON.parse(JSON.stringify(value));
}

function countItemsByType(items = [], type) {
  return items.filter((item) => item.type === type).length;
}

export function buildCourseWithItems(course = {}, items = []) {
  const nextItems = clone(items) || [];
  const details = course.details && typeof course.details === 'object'
    ? {
      ...course.details,
      items: nextItems,
      lessons: nextItems,
    }
    : course.details;

  return {
    ...course,
    items: nextItems,
    lessons: nextItems,
    details,
    lessonCount: countItemsByType(nextItems, 'lesson'),
    quizCount: countItemsByType(nextItems, 'quiz'),
    gameCount: countItemsByType(nextItems, 'game'),
    itemsLoaded: true,
  };
}

function updateCourseItems(state, { courseId, items = [] } = {}) {
  const targetCourseId = String(courseId || '');
  if (!targetCourseId) return;

  state.courses = state.courses.map((course) => (
    String(course.id) === targetCourseId
      ? buildCourseWithItems(course, items)
      : course
  ));
}

function upsertCourse(state, course = {}) {
  state.courses = upsertEntities(state.courses, course);
}

function getEntityId(entity = {}) {
  return entity?.id || entity?.itemId || '';
}

function upsertEntities(currentEntities = [], nextEntities = []) {
  const nextEntityList = Array.isArray(nextEntities) ? nextEntities : [nextEntities];
  const entityById = new Map((clone(currentEntities) || []).map((entity) => [String(getEntityId(entity)), entity]));
  const entityOrder = (clone(currentEntities) || [])
    .map((entity) => String(getEntityId(entity)))
    .filter(Boolean);

  nextEntityList
    .filter(Boolean)
    .forEach((entity) => {
      const entityId = String(getEntityId(entity));

      if (!entityId) return;
      if (!entityById.has(entityId)) {
        entityOrder.push(entityId);
      }

      entityById.set(entityId, clone(entity));
    });

  return entityOrder.map((entityId) => entityById.get(entityId)).filter(Boolean);
}

function findById(entities = [], entityId) {
  return entities.find((entity) => String(entity?.id || '') === String(entityId || '')) || null;
}

function filterByGroupName(entities = [], groupName) {
  return entities.filter((entity) => String(entity?.groupName || '') === String(groupName || ''));
}

function initialState(initialState = {}) {
  return {
    courses: clone(initialState.courses || []),
    sectionItems: clone(initialState.sectionItems || []),
    quizGames: clone(initialState.quizGames || []),
    quizGamesLoaded: Boolean(initialState.quizGamesLoaded),
    quizCases: clone(initialState.quizCases || []),
    quizCaseOptions: clone(initialState.quizCaseOptions || []),
    quizCaseOptionsLoaded: Boolean(initialState.quizCaseOptionsLoaded),
    quizGameContentLoadedIds: clone(initialState.quizGameContentLoadedIds || []),
    tags: clone(initialState.tags || []),
    tagsLoaded: Boolean(initialState.tagsLoaded),
  };
}

const educationStoreModule = {
  namespaced: true,
  state: initialState,
  getters: {
    getCourses: (state) => state.courses,
    getCourseById: (state) => (courseId) => (
      state.courses.find((course) => String(course.id) === String(courseId)) || null
    ),
    getSectionItems: (state) => state.sectionItems,
    getQuizGames: (state) => state.quizGames,
    areQuizGamesLoaded: (state) => state.quizGamesLoaded,
    getQuizCases: (state) => state.quizCases,
    getQuizCaseOptions: (state) => state.quizCaseOptions,
    areQuizCaseOptionsLoaded: (state) => state.quizCaseOptionsLoaded,
    getTags: (state) => state.tags,
    areTagsLoaded: (state) => state.tagsLoaded,
    getQuizGameById: (state) => (quizGameId) => findById(state.quizGames, quizGameId),
    getQuizCasesByGroupName: (state) => (groupName) => filterByGroupName(state.quizCases, groupName),
    getQuizCaseOptionsByGroupName: (state) => (groupName) => filterByGroupName(state.quizCaseOptions, groupName),
    isQuizGameContentLoaded: (state) => (quizGameId) => (
      state.quizGameContentLoadedIds.includes(String(quizGameId || ''))
    ),
  },
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
    SET_COURSES(state, courses = []) {
      state.courses = clone(courses) || [];
    },
    UPSERT_COURSE: upsertCourse,
    SET_SECTION_ITEMS(state, sectionItems = []) {
      state.sectionItems = clone(sectionItems) || [];
    },
    SET_QUIZ_GAMES(state, quizGames = []) {
      state.quizGames = clone(quizGames) || [];
      state.quizGamesLoaded = true;
    },
    MARK_QUIZ_GAMES_LOADED(state) {
      state.quizGamesLoaded = true;
    },
    SET_QUIZ_CASES(state, quizCases = []) {
      state.quizCases = clone(quizCases) || [];
    },
    SET_QUIZ_CASE_OPTIONS(state, quizCaseOptions = []) {
      state.quizCaseOptions = clone(quizCaseOptions) || [];
      state.quizCaseOptionsLoaded = true;
    },
    SET_TAGS(state, tags = []) {
      state.tags = clone(tags) || [];
      state.tagsLoaded = true;
    },
    UPSERT_QUIZ_GAMES(state, quizGames = []) {
      state.quizGames = upsertEntities(state.quizGames, quizGames);
    },
    UPSERT_QUIZ_CASES(state, quizCases = []) {
      state.quizCases = upsertEntities(state.quizCases, quizCases);
    },
    UPSERT_QUIZ_CASE_OPTIONS(state, quizCaseOptions = []) {
      state.quizCaseOptions = upsertEntities(state.quizCaseOptions, quizCaseOptions);
    },
    MARK_QUIZ_GAME_CONTENT_LOADED(state, quizGameId) {
      const id = String(quizGameId || '');

      if (!id || state.quizGameContentLoadedIds.includes(id)) return;

      state.quizGameContentLoadedIds = [...state.quizGameContentLoadedIds, id];
    },
    SET_COURSE_ITEMS: updateCourseItems,
    REORDER_COURSE_ITEMS: updateCourseItems,
  },
  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },
    setCourses({ commit }, courses = []) {
      commit('SET_COURSES', courses);
    },
    upsertCourse({ commit }, course = {}) {
      commit('UPSERT_COURSE', course);
    },
    setCourseItems({ commit }, payload) {
      commit('SET_COURSE_ITEMS', payload);
    },
    reorderCourseItems({ commit }, payload) {
      commit('REORDER_COURSE_ITEMS', payload);
    },
    setSectionItems({ commit }, sectionItems = []) {
      commit('SET_SECTION_ITEMS', sectionItems);
    },
    setQuizGames({ commit }, quizGames = []) {
      commit('SET_QUIZ_GAMES', quizGames);
    },
    setQuizCases({ commit }, quizCases = []) {
      commit('SET_QUIZ_CASES', quizCases);
    },
    setQuizCaseOptions({ commit }, quizCaseOptions = []) {
      commit('SET_QUIZ_CASE_OPTIONS', quizCaseOptions);
    },
    setTags({ commit }, tags = []) {
      commit('SET_TAGS', tags);
    },
    upsertQuizGames({ commit }, quizGames = []) {
      commit('UPSERT_QUIZ_GAMES', quizGames);
    },
    upsertQuizCases({ commit }, quizCases = []) {
      commit('UPSERT_QUIZ_CASES', quizCases);
    },
    upsertQuizCaseOptions({ commit }, quizCaseOptions = []) {
      commit('UPSERT_QUIZ_CASE_OPTIONS', quizCaseOptions);
    },
  },
};

export default educationStoreModule;
